import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileText, Loader2, Trash2, Sparkles, ArrowRight, Camera } from "lucide-react";
import { CameraScanner } from "@/components/CameraScanner";

const STAGE_KEYS = ["stage_uploading", "stage_classifying", "stage_reading", "stage_coverage", "stage_exclusions", "stage_summary"];

export default function Documents() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [stage, setStage] = useState(0);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const inputRef = useRef();
  const stageTimer = useRef();

  const load = useCallback(() => { api.get("/documents").then((r) => setDocs(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (location.state?.openUpload) inputRef.current?.click(); }, [location.state]);

  const runStages = () => {
    setStage(0);
    let i = 0;
    stageTimer.current = setInterval(() => { i = Math.min(i + 1, STAGE_KEYS.length - 1); setStage(i); }, 1400);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    runStages();
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      clearInterval(stageTimer.current);
      toast.success(t("doc_analyzed"));
      load();
      if (res.data.analysis) navigate(`/analyze/${res.data.id}`);
      else toast.message(t("doc_no_text"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("upload_failed"));
    } finally {
      clearInterval(stageTimer.current);
      setUploading(false);
    }
  };

  const del = async (id) => { await api.delete(`/documents/${id}`); load(); };

  const submitScan = async (files) => {
    setScanning(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const res = await api.post("/documents/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("scan_analyzed"));
      setScanOpen(false);
      load();
      if (res.data.analysis) navigate(`/analyze/${res.data.id}`);
      else toast.message(t("scan_failed"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("scan_failed"));
    } finally {
      setScanning(false);
    }
  };

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy">{t("documents")}</h1>
      <p className="mt-1 text-slate-500">{t("documents_sub")}</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div
            data-testid="upload-dropzone"
            onClick={() => !uploading && inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files[0]); }}
            className="cursor-pointer rounded-3xl border-2 border-dashed border-slate-200 bg-white p-8 text-center hover:border-brandblue transition-colors">
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-brandblue" />
                <p className="font-medium text-navy">{t("analyzing")}</p>
                <div className="w-full space-y-1.5 mt-2">
                  {STAGE_KEYS.map((s, i) => (
                    <div key={s} className={`flex items-center gap-2 text-sm ${i <= stage ? "text-brandblue" : "text-slate-300"}`}>
                      {i < stage ? <span className="h-1.5 w-1.5 rounded-full bg-covered" /> : i === stage ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="h-1.5 w-1.5 rounded-full bg-slate-200" />}
                      {t(s)}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl brand-gradient text-white"><UploadCloud className="h-7 w-7" /></span>
                <p className="mt-4 font-semibold text-navy">{t("drop_document")}</p>
                <p className="text-sm text-slate-400 mt-1">{t("pdf_or_photo")}</p>
                <Button className="mt-4 rounded-full brand-gradient text-white">{t("choose_file")}</Button>
              </>
            )}
            <input ref={inputRef} type="file" hidden accept=".pdf,.txt,.png,.jpg,.jpeg,.webp,.docx" data-testid="file-input"
              onChange={(e) => upload(e.target.files[0])} />
          </div>

          {!uploading && (
            <button data-testid="scan-photo-btn" onClick={() => setScanOpen(true)}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-brandteal/40 bg-brandteal/5 py-4 text-base font-semibold text-navy transition-colors hover:bg-brandteal/10">
              <Camera className="h-5 w-5 text-brandteal" /> {t("scan_photo")}
            </button>
          )}
        </div>

        <div className="lg:col-span-2 space-y-3">
          {docs.length === 0 && <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center text-slate-400">{t("no_documents")}</div>}
          {docs.map((d) => (
            <div key={d.id} data-testid={`doc-row-${d.id}`} className="flex items-center gap-4 rounded-2xl bg-white border border-slate-100 card-shadow p-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-brandblue"><FileText className="h-5 w-5" /></span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-navy truncate">{d.original_filename}</p>
                <p className="text-xs text-slate-400">
                  {d.analysis?.insurance_type ? `${d.analysis.insurance_type} • ` : ""}
                  {new Date(d.created_at).toLocaleDateString()}
                  {d.analysis && <span className="ml-2 inline-flex items-center gap-1 text-brandteal"><Sparkles className="h-3 w-3" /> {t("analyzed")}</span>}
                </p>
              </div>
              {d.analysis && (
                <button onClick={() => navigate(`/analyze/${d.id}`)} data-testid={`doc-open-${d.id}`}
                  className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3.5 py-2 text-sm font-medium text-brandblue hover:bg-blue-100">
                  {t("open")} <ArrowRight className="h-4 w-4" />
                </button>
              )}
              <button onClick={() => del(d.id)} data-testid={`doc-del-${d.id}`} className="rounded-full p-2 text-slate-300 hover:text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <CameraScanner open={scanOpen} onClose={() => setScanOpen(false)} onSubmit={submitScan} submitting={scanning} />
    </AppLayout>
  );
}
